import { useQuery } from '@apollo/client';
import { Arrow } from '@egjs/flicking-plugins';
import Flicking, { Plugin, ViewportSlot } from '@egjs/react-flicking';
import ThumbnailImage from '@flight-workspace/common-client/src/components/thumbnailImage';
import {
  getCookie,
  setCookie,
} from '@flight-workspace/common-client/src/util/cookie';
import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import SendBeacon from 'common/sendBeacon';
import config from 'config/index';
import dayjs from 'dayjs';
import useAirportDetailList from 'hooks/useAirportDetailList';
import { useRef, useState } from 'react';
import styles from './index.module.scss';
import PromotionBanners from './promotionBanners';
import GET_STATS_RECOMMENDED_HOTELS from './statsRecommendedHotels.gql';
import Title from './title';
import { IStatsRecommendedHotels, ImpSection, RecoType } from './types';

const cx = classNames.bind(styles);

export const TOP_CLK_RECOMMENDED_HOTELS_INDEX = 3;
export const DOMESTIC_CVR_RECOMMENDED_HOTELS_INDEX = 10;
export const INTERNATIONAL_CVR_RECOMMENDED_HOTELS_INDEX = 10;

const configMap = new Map([
  [
    RecoType.CVR,
    {
      clickCodes: {
        click: 'cvrrecohotelclick',
        close: 'cvrrecohoteltodayclosed',
        info: 'cvrrecohotelinfo',
      },
      cookieName: 'RECOMMENDED_HOTELS_CVR',
      impSection: ImpSection.FLIGHT_DETAIL2,
      tooltip: '예약 전환율이 높은 호텔을 보여줍니다.',
    },
  ],
  [
    RecoType.TOP_CLK,
    {
      clickCodes: {
        click: 'recohotelclick',
        close: 'recohoteltodayclosed',
        info: 'recohotelinfo',
      },
      cookieName: 'RECOMMENDED_HOTELS_TOP_CLK',
      impSection: ImpSection.FLIGHT_DETAIL,
      tooltip:
        '동일 성별/연령 그룹에서 인기 많은 호텔을 보여줍니다. 비로그인 시 전체 인기순 호텔 결과가 제공됩니다.',
    },
  ],
]);

interface IProps {
  areaCode: string;
  arrivalAirport: string;
  departureDate: string;
  recoType: RecoType;
  adultCnt?: number;
  childCnt?: number;
  includeTax?: boolean;
}

interface IData {
  statsRecommendedHotels: IStatsRecommendedHotels;
}

export default function StatsRecommendedHotels({
  areaCode,
  arrivalAirport,
  departureDate,
  recoType,
  adultCnt,
  childCnt,
  includeTax,
}: IProps) {
  const checkIn = dayjs(departureDate, 'YYYYMMDD');
  const checkOut = checkIn.add(1, 'day');
  const { clickCodes, cookieName, impSection, tooltip } =
    configMap.get(recoType)!;
  const { data: { airportDetailList } = {} } = useAirportDetailList([
    arrivalAirport,
  ]);
  const { data: { statsRecommendedHotels } = {} } = useQuery<IData>(
    GET_STATS_RECOMMENDED_HOTELS,
    {
      variables: {
        checkIn: checkIn.format('YYYY-MM-DD'),
        checkOut: checkOut.format('YYYY-MM-DD'),
        iataCode: arrivalAirport,
        impSection,
        recoType,
        adultCnt,
        childCnt,
        includeTax,
      },
    },
  );
  const plugins = useRef([
    new Arrow({
      moveByViewportSize: true,
      prevElSelector: '._prev',
      nextElSelector: '._next',
    }) as unknown as Plugin,
  ]);
  const [open, setOpen] = useState(!getCookie(cookieName));

  if (
    !open ||
    !airportDetailList ||
    !statsRecommendedHotels ||
    statsRecommendedHotels.hotelsInfo.length < 2
  ) {
    return null;
  }

  function hideToday() {
    setOpen(false);
    setCookie(cookieName, 'true', {
      expires: dayjs().add(1, 'day').startOf('day').toDate().toUTCString(),
    });
  }

  const [{ cityName }] = airportDetailList;
  return (
    <div
      className={cx('StatsRecommendedHotels')}
      data-display-device={
        statsRecommendedHotels.hotelsInfo.length >= 7 ? 'all' : 'mobile'
      }
    >
      <h3 className={cx('title')}>
        <Title cityName={cityName} recoType={recoType} />
        <details
          className={cx('info')}
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.${clickCodes.info}`}
        >
          <summary>
            <i className={cx('icon')} title="여행상품 PICK" />
          </summary>
          <div role="tooltip">{tooltip}</div>
        </details>
      </h3>
      <SendBeacon url={statsRecommendedHotels.impLogUrl} />
      <Flicking
        className={cx('list')}
        align={{ camera: '0px', panel: '0px' }}
        bound
        plugins={plugins.current}
        preventDefaultOnDrag
      >
        {statsRecommendedHotels.hotelsInfo.map(
          ({ clkLogUrl, id, image, name, overallRating, price }) => (
            <div className={cx('item')} key={id}>
              <picture className={cx('thumb')}>
                <ThumbnailImage
                  alt={name}
                  thumbnail={config.thumbnail}
                  src={image}
                  type="f300_400"
                />
              </picture>
              {!overallRating ? (
                <b className={cx('rate', 'item_info', 'as_empty')}>평점없음</b>
              ) : (
                <b className={cx('rate', 'item_info')}>{overallRating}</b>
              )}
              <div className={cx('name')}>{name}</div>
              <span className={cx('price')}>
                <b className={cx('value')}>{withPriceSeperator(price)}</b>
                <span className={cx('unit')}>원~</span>
              </span>
              <a
                className={cx('anchor')}
                href={clkLogUrl}
                target="_blank"
                data-event-nlog="click"
                data-event-area-code={`${areaCode}.${clickCodes.click}`}
                rel="noreferrer"
              />
            </div>
          ),
        )}
        <ViewportSlot>
          <button
            type="button"
            className={cx('prev', '_prev')}
            aria-label="이전"
          />
          <button
            type="button"
            className={cx('next', '_next')}
            aria-label="다음"
          />
        </ViewportSlot>
      </Flicking>
      <PromotionBanners />
      <div className={cx('footer')}>
        <button
          type="button"
          className={cx('close')}
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.${clickCodes.close}`}
          onClick={hideToday}
        >
          오늘 하루 보지 않기
        </button>
      </div>
    </div>
  );
}

export { RecoType };
